import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  makeStyles,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import React, { useEffect, useState, useMemo } from 'react'
import Drawer from 'src/components/ui/Drawer'
import yup from 'src/components/yupCustom'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
  showNotification,
  showNotificationErrorAPI
} from 'src/core/helpers/notification'
import UBox from 'src/components/ui/Ubox'
import TextField from 'src/components/ui/TextField'
import { Autocomplete } from 'src/components/ui/Autocomplete'
import Alert from '@material-ui/lab/Alert'
import formaPagamento from 'src/core/consts/formaPagamento'
import meioPagamento, { MEIO_PAGAMENTO } from 'src/core/consts/meioPagamento'
import HourInput from 'src/components/ui/HourInput'
import moment from 'moment'
import CurrencyInput from 'src/components/ui/CurrencyInput'
import usePagamento from 'src/hooks/usePagamento'
import Button from 'src/components/ui/Button'
import { IPagamentoAvulsoRequest } from 'src/contexts/Pagamento/context'
import {
  ehEtapaUnicaParaExibir,
  etapaEstaPersistida,
  obterEtapasParaExibir,
  Pagamento
} from 'src/api/pagamento'
import {
  formatarData,
  formatarHoraLocal
} from 'src/core/formatacoes/formatarData'
import formatarDinheiro from 'src/core/formatacoes/formatarDinheiro'
import { DropzoneArea } from 'material-ui-dropzone'
import { FaRegFilePdf } from 'react-icons/fa'

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiDropzoneArea-root': {
      minHeight: '160px'
    },
    '& .MuiDropzoneArea-text': {
      marginBottom: 0
    },
    '& .MuiDropzoneArea-text.MuiTypography-h5': {
      fontSize: '1.1rem',
      padding: '16px 32px 8px'
    },
    '& .MuiDropzoneArea-icon': {
      color: 'rgba(100, 100, 100, 0.87)'
    }
  },
  etapaCard: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#fff',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: '#f9fcff'
    }
  },
  etapaCardSelected: {
    borderColor: `${theme.palette.primary.main} !important`,
    backgroundColor: '#f0f7ff !important',
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  },
  etapaCardDisabled: {
    backgroundColor: '#f5f5f5 !important',
    borderColor: '#e0e0e0 !important',
    cursor: 'not-allowed !important',
    opacity: 0.7
  },
  resumoBox: {
    backgroundColor: '#f4f6f8',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
    border: '1px solid #e2e8f0'
  },
  preencherBtn: {
    fontSize: '0.75rem',
    padding: '2px 8px',
    textTransform: 'none',
    marginLeft: 'auto'
  }
}))

const stylePDF = {
  backgroundSize: 'cover',
  backgroundPosition: 'center center',
  backgroundRepeat: 'no-repeat',
  border: '1px solid #ddd',
  borderRadius: '8px',
  boxShadow: '3px 3px 3px #eee',
  cursor: 'pointer',
  width: '150px',
  height: '150px',
  display: 'flex'
}

const styleComp = {
  backgroundSize: 'cover',
  backgroundPosition: 'center center',
  backgroundRepeat: 'no-repeat',
  border: '1px solid #ddd',
  borderRadius: '8px',
  boxShadow: '3px 3px 3px #eee',
  cursor: 'pointer',
  width: '150px',
  height: '150px'
}

const schema = yup.object().shape({
  formaPagamento: yup
    .object()
    .nullable()
    .required('A forma de pagamento é obrigatória')
    .label('Forma de Pagamento'),
  codigoTransacao: yup
    .string()
    .nullable()
    .when('meioPagamento', {
      is: (tipo: any) => tipo?.value === MEIO_PAGAMENTO.MAQUININHA.value,
      then: yup.string().nullable().required('Código de transação é obrigatório para maquininha').label('Código de transação')
    })
    .label('Código de transação'),
  meioPagamento: yup.object().nullable().required('O meio de pagamento é obrigatório').label('Meio de Pagamento'),
  valor: yup
    .number()
    .nullable()
    .required('O valor é obrigatório')
    .moreThan(0, 'O valor deve ser maior que zero')
    .label('Valor'),
  juros: yup.number().nullable().label('Juros'),
  dataPagamento: yup
    .string()
    .required('Data de pagamento é obrigatória')
    .test('data-valida', 'Por favor escolha uma data válida', function (value) {
      if (!value || value.replace(' ', '').length < 10) return false
      const data = moment(value, 'DD/MM/YYYY')
      return data.isValid()
    })
    .label('Data de pagamento'),
  horaPagamento: yup
    .string()
    .required('Horário do pagamento é obrigatório')
    .test('hora-valida', 'Por favor escolha um horário válido', function (value) {
      const date = this.parent.dataPagamento
      if (!date || date.replace(' ', '').length < 10) return false
      return true
    })
    .label('Horário de pagamento')
})

interface ObjetoGenerico {
  id: number
  value: string
  name: string
}

interface PagamentoAvulsoForm {
  dataPagamento: string
  horaPagamento: string
  formaPagamento: ObjetoGenerico
  meioPagamento: ObjetoGenerico
  codigoTransacao: string
  valor: number
  juros?: number
  urlComprovante?: string
}

interface Props {
  pedido?: Pagamento
  propostaId: string
  open: boolean
  handleOnClose: () => void
  etapaIndexDefault?: number
}

type TModeloEtapa = 'multiplas_etapas' | 'etapa_unica'

export default function DrawerPagamentoAvulso({
  pedido,
  open,
  propostaId,
  handleOnClose,
  etapaIndexDefault
}: Props) {
  const classes = useStyles()
  const { pagamentoAvulso, loadingAvulso, resumo } = usePagamento()
  const [mostrarAnexo, setMostrarAnexo] = useState(!pedido?.urlComprovante)
  const [arquivo, setArquivo] = useState<File>(null)

  // Template v2.1 com 2+ etapas: permite o rádio só enquanto nada persistido foi iniciado
  const temMultiplasEtapas = useMemo(() => {
    return resumo?.versaoPagamento === 'v2.1' && (resumo?.etapas?.length || 0) > 1
  }, [resumo])

  const etapasParaExibir = useMemo(() => obterEtapasParaExibir(resumo), [resumo])
  const modeloExibidoEhEtapaUnica = useMemo(
    () => ehEtapaUnicaParaExibir(resumo),
    [resumo]
  )
  const modeloExibidoPersistido = useMemo(
    () => etapasParaExibir.some(e => etapaEstaPersistida(e.statusEtapa)),
    [etapasParaExibir]
  )

  const temEtapasDefinitivas = Boolean(resumo?.existeEtapasDB)

  const bloqueadoEmMultiplasEtapas = useMemo(() => {
    if (!temMultiplasEtapas) return false
    return modeloExibidoPersistido && !modeloExibidoEhEtapaUnica
  }, [temMultiplasEtapas, modeloExibidoPersistido, modeloExibidoEhEtapaUnica])

  const bloqueadoEmEtapaUnica = useMemo(() => {
    if (!temMultiplasEtapas) return true
    return modeloExibidoPersistido && modeloExibidoEhEtapaUnica
  }, [temMultiplasEtapas, modeloExibidoPersistido, modeloExibidoEhEtapaUnica])

  const etapaDoPedidoIndex = useMemo(() => {
    if (!pedido) return -1
    if (resumo?.etapaUnica?.pedidos?.some(p => p.id === pedido.id)) return -1
    if (!resumo?.etapas?.length) return -1
    return resumo.etapas.findIndex(
      e =>
        etapaEstaPersistida(e.statusEtapa) &&
        e.pedidos?.some(p => p.id === pedido.id)
    )
  }, [pedido, resumo])

  const [modelo, setModelo] = useState<TModeloEtapa>(() => {
    if (!temMultiplasEtapas) return 'etapa_unica'
    if (pedido) {
      return etapaDoPedidoIndex !== -1 ? 'multiplas_etapas' : 'etapa_unica'
    }
    if (bloqueadoEmMultiplasEtapas) return 'multiplas_etapas'
    if (bloqueadoEmEtapaUnica) return 'etapa_unica'
    return 'multiplas_etapas'
  })

  useEffect(() => {
    if (!pedido) {
      if (!temMultiplasEtapas) {
        setModelo('etapa_unica')
      } else if (bloqueadoEmMultiplasEtapas) {
        setModelo('multiplas_etapas')
      } else if (bloqueadoEmEtapaUnica) {
        setModelo('etapa_unica')
      }
    }
  }, [temMultiplasEtapas, bloqueadoEmMultiplasEtapas, bloqueadoEmEtapaUnica, pedido])

  // Encontra a primeira etapa pendente com saldo a pagar
  const primeiraEtapaPendenteIndex = useMemo(() => {
    if (!resumo?.etapas?.length) return 0
    const idx = resumo.etapas.findIndex(e => e.saldoDevedor > 0.01)
    return idx !== -1 ? idx : 0
  }, [resumo])

  const [etapaSelecionadaIndex, setEtapaSelecionadaIndex] = useState<number>(() => {
    if (pedido && etapaDoPedidoIndex !== -1) {
      return etapaDoPedidoIndex
    }
    if (etapaIndexDefault !== undefined && etapaIndexDefault !== null) {
      return etapaIndexDefault
    }
    return primeiraEtapaPendenteIndex
  })

  // Dados da etapa atualmente selecionada
  const etapaSelecionada = useMemo(() => {
    if (modelo !== 'multiplas_etapas' || !resumo?.etapas?.length) return null
    return resumo.etapas[etapaSelecionadaIndex] || null
  }, [modelo, resumo, etapaSelecionadaIndex])

  // Saldo máximo disponível para pagamento no modelo atual
  const saldoMaximo = useMemo(() => {
    if (modelo === 'multiplas_etapas') {
      if (!etapaSelecionada) return 0
      let saldo = etapaSelecionada.saldoDevedor
      // Se estiver editando um pedido desta etapa, devolver o valor do pedido para o saldo disponível
      if (pedido && etapaDoPedidoIndex === etapaSelecionadaIndex) {
        if (pedido.status?.toLowerCase() === 'pago' || pedido.status?.toLowerCase() === 'paid') {
          saldo += pedido.valor
        }
      }
      return Math.max(0, saldo)
    } else {
      let saldo = resumo?.saldoDevedor ?? 0
      if (pedido) {
        if (pedido.status?.toLowerCase() === 'pago' || pedido.status?.toLowerCase() === 'paid') {
          saldo += pedido.valor
        }
      }
      return Math.max(0, saldo)
    }
  }, [modelo, etapaSelecionada, resumo, pedido, etapaDoPedidoIndex, etapaSelecionadaIndex])

  const pagamentoAvulsoDefault: PagamentoAvulsoForm = {
    dataPagamento: '',
    horaPagamento: '',
    codigoTransacao: '',
    formaPagamento: null,
    meioPagamento: null,
    valor: 0,
    juros: null
  }

  function mapearPedidoParaForm() {
    if (!pedido) return pagamentoAvulsoDefault
    const dataPag = formatarData(pedido.dataPagamento)
    const horaPag = formatarHoraLocal(pedido.dataPagamento)
    const forma = formaPagamento.find(f => f.value === pedido.formaPagamento)
    const tipo = meioPagamento.find(f => f.value === pedido.tipoPagamento)
    const form: PagamentoAvulsoForm = {
      ...pedido,
      dataPagamento: dataPag,
      horaPagamento: horaPag,
      formaPagamento: forma,
      meioPagamento: tipo,
      codigoTransacao: pedido?.codigoEstabelecimento || pedido?.codigoTransacao || ''
    }

    form.valor = parseInt((pedido.valor * 100).toFixed())
    form.juros = pedido.juros ? parseInt((pedido.juros * 100).toFixed()) : null
    return form
  }

  const {
    watch,
    register,
    setValue,
    errors,
    control,
    handleSubmit
  } = useForm<PagamentoAvulsoForm>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: !!pedido ? mapearPedidoParaForm() : pagamentoAvulsoDefault
  })

  const command = watch()

  const handleItemValue = (name: keyof PagamentoAvulsoForm, item: any) => {
    if (item?.id !== undefined) {
      setValue(name, item, { shouldValidate: true })
    } else {
      setValue(name, null, { shouldValidate: true })
    }
  }

  const handleValue = (name: keyof PagamentoAvulsoForm, item: any) => {
    setValue(name, item, { shouldValidate: true })
  }

  function handleChangeValor(key: keyof PagamentoAvulsoForm, valor: number) {
    let valorFormatado = valor / 100
    if (!valorFormatado || valorFormatado < 0) {
      valor = 0
    }
    setValue(key, valor, { shouldValidate: true })
  }

  function atualizarArquivo(files: File[]) {
    setArquivo(files[0] || null)
  }

  function handlePreencherSaldoTotal() {
    if (saldoMaximo > 0) {
      setValue('valor', Math.round(saldoMaximo * 100), { shouldValidate: true })
    }
  }

  const onSubmit = async (data: PagamentoAvulsoForm) => {
    const valorEmReais = data.valor / 100

    if (modelo === 'multiplas_etapas' && etapaSelecionada) {
      const ehEtapaQuitada = etapaSelecionada.saldoDevedor <= 0.01 && (!pedido || etapaDoPedidoIndex !== etapaSelecionadaIndex)
      if (ehEtapaQuitada) {
        showNotification(
          'Etapa 100% Paga',
          'Não é possível adicionar pagamentos a uma etapa que já está 100% paga.',
          'warning',
          true
        )
        return
      }
    }

    const juros = !data.juros ? 0 : data.juros / 100
    const meio = data.meioPagamento?.value
    const forma = data.formaPagamento?.value
    const isEtapaUnica = modelo === 'etapa_unica'

    const novoPagamento: IPagamentoAvulsoRequest = {
      ...data,
      propostaId,
      meioPagamento: meio,
      formaPagamento: forma,
      juros,
      valor: valorEmReais,
      etapaUnica: isEtapaUnica,
      etapa: isEtapaUnica ? undefined : etapaSelecionadaIndex,
      tipoEtapa: isEtapaUnica ? 1 : 2
    }

    if (!!pedido) {
      novoPagamento.pedidoId = pedido.id
    }

    const dataHoraPagamento = moment(
      `${data.dataPagamento} ${data.horaPagamento}`,
      'DD/MM/YYYY HH:mm'
    )
    novoPagamento.dataPagamento = dataHoraPagamento.isValid()
      ? dataHoraPagamento.toISOString()
      : moment().toISOString()

    if (!!arquivo) {
      novoPagamento.arquivo = arquivo
    }

    try {
      await pagamentoAvulso(novoPagamento)
      showNotification(
        'Sucesso',
        `Registro de pagamento manual ${!pedido ? 'adicionado' : 'atualizado'} com sucesso.`,
        'success',
        true
      )
      handleOnClose()
    } catch (error) {
      // O erro já é tratado e notificado pelo provider
    }
  }

  useEffect(() => {
    register('formaPagamento')
    register('meioPagamento')
    register('horaPagamento')
    register('valor')
    register('juros')
  }, [register])

  return (
    <Drawer
      open={open}
      anchor="right"
      handleOnClose={handleOnClose}
      titulo={`${!pedido ? 'Novo' : 'Editar'} pagamento avulso`}
    >
      <UBox component="form" onSubmit={handleSubmit(onSubmit)}>
        {/* Seção 1: Modelo do Pagamento (Etapa Única vs Múltiplas Etapas) */}
        {!temMultiplasEtapas && (
          <Box mb={2}>
            <Alert severity="info">
              Esta proposta está configurada no modelo de <b>Etapa Única (À vista)</b>.
            </Alert>
          </Box>
        )}

        {temMultiplasEtapas && (
          <Box mb={2}>
            {bloqueadoEmMultiplasEtapas && (
              <Box mb={1}>
                <Alert severity="info">
                  Esta proposta {temEtapasDefinitivas ? 'possui etapas definitivas' : 'já possui etapas registradas'} no modelo de <b>Múltiplas Etapas</b>.
                </Alert>
              </Box>
            )}
            {bloqueadoEmEtapaUnica && (
              <Box mb={1}>
                <Alert severity="info">
                  Esta proposta {temEtapasDefinitivas ? 'possui etapas definitivas' : 'já possui registro'} no modelo de <b>Etapa Única</b>.
                </Alert>
              </Box>
            )}

            {!bloqueadoEmMultiplasEtapas && !bloqueadoEmEtapaUnica && (
              <FormControl component="fieldset" fullWidth style={{ marginBottom: 8 }}>
                <FormLabel component="legend" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568' }}>
                  Modelo do pagamento
                </FormLabel>
                <RadioGroup
                  row
                  value={modelo}
                  onChange={e => {
                    const novoModelo = e.target.value as TModeloEtapa
                    setModelo(novoModelo)
                  }}
                >
                  <FormControlLabel
                    value="multiplas_etapas"
                    control={<Radio color="primary" size="small" />}
                    label="Múltiplas Etapas"
                  />
                  <FormControlLabel
                    value="etapa_unica"
                    control={<Radio color="primary" size="small" />}
                    label="Etapa Única (À vista)"
                  />
                </RadioGroup>
              </FormControl>
            )}
          </Box>
        )}

        {/* Seção 2: Seleção de Etapa quando modelo for Múltiplas Etapas */}
        {temMultiplasEtapas && modelo === 'multiplas_etapas' && (
          <Box mb={2}>
            <Typography variant="subtitle2" color="primary" style={{ fontWeight: 600, marginBottom: 8 }}>
              Selecione a etapa do pagamento:
            </Typography>

            {resumo?.etapas?.map((etp, idx) => {
              const estaSelecionada = etapaSelecionadaIndex === idx
              const ehEtapaDoPedidoEditado = pedido && etapaDoPedidoIndex === idx
              const quitada = etp.saldoDevedor <= 0.01 && !ehEtapaDoPedidoEditado

              return (
                <div
                  key={idx}
                  className={`${classes.etapaCard} ${estaSelecionada ? classes.etapaCardSelected : ''} ${
                    quitada ? classes.etapaCardDisabled : ''
                  }`}
                  onClick={() => {
                    if (!quitada) {
                      setEtapaSelecionadaIndex(idx)
                    }
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center">
                      <Radio
                        checked={estaSelecionada}
                        disabled={quitada}
                        color="primary"
                        size="small"
                        style={{ padding: '0 8px 0 0' }}
                      />
                      <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                        Etapa {idx + 1}: {etp.titulo}
                      </Typography>
                    </Box>
                    {quitada ? (
                      <Chip label="100% Paga" size="small" style={{ backgroundColor: '#e2e8f0', color: '#718096', fontWeight: 600 }} />
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Saldo: <b style={{ color: '#ca5f21' }}>{formatarDinheiro(etp.saldoDevedor)}</b>
                      </Typography>
                    )}
                  </Box>

                  <Box display="flex" justifyContent="space-between" mt={1} pl={4}>
                    <Typography variant="caption" color="textSecondary">
                      Valor total: {formatarDinheiro(etp.valor)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Já pago: {formatarDinheiro(etp.valorPago)}
                    </Typography>
                  </Box>
                </div>
              )
            })}
          </Box>
        )}

        {/* Seção Resumo da Etapa Selecionada / Proposta */}
        <Box className={classes.resumoBox}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#2d3748' }}>
              {modelo === 'multiplas_etapas' && etapaSelecionada
                ? `Resumo da Etapa ${etapaSelecionadaIndex + 1} (${etapaSelecionada.titulo})`
                : 'Resumo da Proposta (Etapa Única)'}
            </Typography>
            {saldoMaximo > 0 && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                className={classes.preencherBtn}
                onClick={handlePreencherSaldoTotal}
              >
                Preencher saldo
              </Button>
            )}
          </Box>

          <Grid container spacing={1} style={{ marginTop: 4 }}>
            <Grid item xs={4}>
              <Typography variant="caption" color="textSecondary">
                Valor total
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {formatarDinheiro(
                  modelo === 'multiplas_etapas' && etapaSelecionada
                    ? etapaSelecionada.valor
                    : resumo?.valorTotal
                )}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="textSecondary">
                Já pago
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#38a169' }}>
                {formatarDinheiro(
                  modelo === 'multiplas_etapas' && etapaSelecionada
                    ? etapaSelecionada.valorPago
                    : resumo?.valorPago
                )}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="textSecondary">
                Saldo restante
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#ca5f21' }}>
                {formatarDinheiro(saldoMaximo)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Data e Hora */}
        <UBox component={Grid} container xs={12} spacing={1}>
          <Grid item xs={12} md={6}>
            <TextField
              erro={errors}
              control={control}
              name="dataPagamento"
              label="Data do pagamento"
              placeholder="Ex: 10/04/2025"
              mask="99/99/9999"
              value={command.dataPagamento}
              defaultValue={command.dataPagamento}
              InputLabelProps={{
                shrink: true
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <HourInput
              errors={errors}
              name="horaPagamento"
              label="Horário do pagamento"
              value={command.horaPagamento}
              ignorarArrendondamentoMinutos
              control={control}
              handleChange={value => {
                handleValue('horaPagamento', value)
              }}
            />
          </Grid>
        </UBox>

        {/* Valores */}
        <Grid container xs={12} spacing={1}>
          <Grid item xs={6}>
            <FormControl fullWidth size="medium" variant="outlined">
              <CurrencyInput
                fullWidth
                hidePrefix
                erro={errors}
                autoFocus
                variant="outlined"
                name="valor"
                label="Valor"
                hiddenLabel
                control={control}
                type="tel"
                InputLabelProps={{
                  shrink: true
                }}
                max={1000000000}
                onValueChange={value => handleChangeValor('valor', value)}
                value={command?.valor || 0}
              />
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth size="medium" variant="outlined">
              <CurrencyInput
                fullWidth
                hidePrefix
                erro={errors}
                variant="outlined"
                name="juros"
                label="Juros"
                hiddenLabel
                control={control}
                type="tel"
                InputLabelProps={{
                  shrink: true
                }}
                max={1000000000}
                onValueChange={value => handleChangeValor('juros', value)}
                value={command?.juros || 0}
              />
            </FormControl>
          </Grid>
        </Grid>

        {/* Forma e Meio de Pagamento */}
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Autocomplete
              erro={errors}
              name="formaPagamento"
              label="Forma de pagamento"
              placeholder="Informe a forma de pagamento"
              closeOnSelect
              value={command.formaPagamento}
              options={formaPagamento}
              getOptionLabel={option => option.name}
              onChange={(event, newValue) => {
                handleItemValue('formaPagamento', newValue as any)
              }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Autocomplete
              erro={errors}
              name="meioPagamento"
              label="Meio de Pagamento"
              placeholder="Informe o meio de pagamento"
              closeOnSelect
              value={command.meioPagamento}
              options={meioPagamento}
              getOptionLabel={option => option.name}
              onChange={(event, newValue) => {
                handleItemValue('meioPagamento', newValue as any)
              }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              erro={errors}
              margin="normal"
              name="codigoTransacao"
              label="Código de Transação"
              placeholder="Informe o código"
              type="text"
              inputRef={register}
              InputLabelProps={{
                shrink: true
              }}
            />
          </Grid>
        </Grid>

        {/* Anexo de Comprovante */}
        <Grid container spacing={1}>
          <Grid item xs={12} className={classes.root}>
            {mostrarAnexo ? (
              <UBox mb={3}>
                <Box mt={2}>
                  <Typography variant="subtitle2">
                    Insira arquivos do tipo JPG, JPEG, PNG ou PDF
                  </Typography>
                </Box>
                <DropzoneArea
                  onChange={atualizarArquivo}
                  dropzoneText="Clique aqui ou arraste para anexar um comprovante de pagamento."
                  acceptedFiles={[
                    'image/jpeg, image/png, image/jpg, application/pdf,'
                  ]}
                  onAlert={(message, variant) =>
                    console.log(`${variant}: ${message}`)
                  }
                  showPreviews={true}
                  previewText="Comprovante:"
                  showPreviewsInDropzone={false}
                  filesLimit={1}
                  showAlerts={[]}
                  alertSnackbarProps={{
                    anchorOrigin: { vertical: 'bottom', horizontal: 'center' }
                  }}
                  onDropRejected={e => {
                    showNotification('Ops...', 'Arquivo inválido', 'warning')
                  }}
                />
              </UBox>
            ) : (
              <UBox mb={1}>
                <Typography variant="subtitle2">Comprovante:</Typography>
                {pedido?.urlComprovante?.includes('pdf') ? (
                  <div
                    onClick={() => window.open(pedido.urlComprovante, '_blank')}
                    style={{
                      fontSize: '100px',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'red',
                      ...stylePDF
                    }}
                  >
                    <FaRegFilePdf />
                    <p
                      style={{
                        fontSize: '14px',
                        margin: 0,
                        marginTop: 4,
                        padding: 0,
                        paddingTop: 0,
                        fontWeight: 600
                      }}
                    >
                      Baixar PDF
                    </p>
                  </div>
                ) : (
                  <div
                    onClick={() => window.open(pedido.urlComprovante, '_blank')}
                    style={{
                      background: `url(${pedido?.urlComprovante})`,
                      ...styleComp
                    }}
                  ></div>
                )}
              </UBox>
            )}
            {!!pedido?.urlComprovante && (
              <UBox mb={2}>
                <Button
                  color="secondary"
                  onClick={() => {
                    setMostrarAnexo(!mostrarAnexo)
                  }}
                >
                  {!mostrarAnexo ? 'Alterar comprovante' : 'Cancelar'}
                </Button>
              </UBox>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            loading={loadingAvulso}
            disabled={loadingAvulso}
          >
            Salvar
          </Button>
        </Grid>
      </UBox>
    </Drawer>
  )
}
